import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CustomerReviews = ({ reviews }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [itemsPerView, setItemsPerView] = useState(2);

  const maxIndex = Math.max(0, reviews?.length - itemsPerView);

  // Update items per view based on screen size
  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1); // Mobile: 1 card
      } else {
        setItemsPerView(2); // Tablet and Desktop: 2 cards
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (!isAutoPlaying || !reviews?.length) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
    }, 4000); // Slide every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, maxIndex, reviews?.length]);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name={index < rating ? 'Star' : 'Star'}
        size={16}
        className={index < rating ? 'text-accent fill-accent' : 'text-muted-foreground'}
      />
    ));
  };

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => prev <= 0 ? maxIndex : prev - 1);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
  };

  const handleDotClick = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
          Advait Tour and Travels Customer Reviews
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {renderStars(5)}
          </div>
          <span className="text-sm font-semibold text-foreground">4.8 / 5.0</span>
        </div>
      </div>

      {/* Subtitle */}
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-2">
          Real Experiences, Real Satisfaction
        </h3>
        <p className="text-gray-600 text-sm md:text-base max-w-3xl mx-auto">
          Curious about the Advait Tour and Travels experience? Hear it straight from our passengers. Discover genuine reviews that showcase the satisfaction, comfort, and reliability they've found in our service. Each ride is more than a journey; it's a testament to our commitment to excellence. Join the chorus of happy travelers who have chosen Advait Tour and Travels for their unforgettable rides.
        </p>
      </div>

      {/* Carousel Container */}
      <div className="relative">
        {/* Navigation Arrows */}
        <button
          onClick={handlePrev}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 md:p-3 transition-all duration-300 hover:scale-110"
          aria-label="Previous review"
        >
          <Icon name="ChevronLeft" size={24} className="text-gray-700" />
        </button>

        <button
          onClick={handleNext}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 md:p-3 transition-all duration-300 hover:scale-110"
          aria-label="Next review"
        >
          <Icon name="ChevronRight" size={24} className="text-gray-700" />
        </button>

        {/* Cards Container */}
        <div className="overflow-hidden mx-8 md:mx-12">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`
            }}
          >
            {reviews?.map((review) => (
              <div
                key={review?.id}
                className="flex-shrink-0 px-2 md:px-3"
                style={{ width: `${100 / itemsPerView}%` }}
              >
                <div className="bg-card rounded-xl border border-border p-4 md:p-6 space-y-3 md:space-y-4 hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden flex-shrink-0">
                      <Image
                        src={review?.avatar}
                        alt={review?.avatarAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base md:text-lg font-semibold text-foreground">
                        {review?.name}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1">
                          {renderStars(review?.rating)}
                        </div>
                        <span className="text-xs text-muted-foreground">{review?.date}</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {review?.comment}
                  </p>
                  
                  <div className="flex items-center gap-2 pt-2">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-xs text-muted-foreground">Verified Booking</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: maxIndex + 1 })?.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                currentIndex === index ? 'w-8 bg-orange-500' : 'w-2 bg-gray-300'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* View All Button */}
      <div className="text-center pt-4">
        <button className="px-6 py-3 text-sm font-semibold text-primary hover:text-primary/80 transition-colors duration-200 flex items-center gap-2 mx-auto">
          <span>View All Reviews</span>
          <Icon name="ChevronRight" size={16} />
        </button>
      </div>
    </div>
  );
};

export default CustomerReviews;